/**
 * English screen text: the source for every other language (PRD 11.5). `es.ts` must have the same shape,
 * so a missing or extra key fails the build. Text with values in it is a function.
 */
export const en = {
  common: {
    back: 'Back',
    backToTree: 'Back to tree',
    close: 'Close',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    loading: 'Loading…',
    tryAgain: 'Try again',
    somethingWrong: 'Something went wrong',
    someone: 'Someone',
    searchByName: 'Search by name',
    searchPeople: 'Search people',
    noMatch: (q: string) => `No one matches “${q}”.`,
    couldntLoadTree: 'Couldn’t load this tree.',
    refreshing: 'Refreshing',
    linkCopied: 'Link copied',
    you: 'You',
    roles: { owner: 'owner', editor: 'editor', viewer: 'viewer' } as Record<string, string>,
    relation: { parent: 'Parent', partner: 'Partner', child: 'Child', sibling: 'Sibling' },
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  },
  auth: {
    signIn: 'Sign in',
    continueWithGoogle: 'Continue with Google',
    orUseEmail: 'or use email',
    yourName: 'Your name',
    email: 'Email',
    password: 'Password',
    createAccount: 'Create account',
    sendResetLink: 'Send reset link',
    forgotPassword: 'Forgot password',
    backToSignIn: 'Back to sign in',
    resetSent: 'If that address has an account, a reset link is on its way.',
    /** Sign-in problems by internal code (docs/error-codes.md); anything else gets `other`. */
    errors: {
      A01: 'That email and password don’t match.',
      A02: 'That email already has an account. Try signing in.',
      A03: 'Choose a password with at least 6 characters.',
      A04: 'Enter a valid email address.',
      A05: 'Too many attempts. Please wait a bit and try again.',
      N01: 'No connection. Check your network and try again.',
      N02: 'No connection. Check your network and try again.',
    } as Record<string, string>,
    otherError: 'Something went wrong. Please try again.',
  },
  whatsNew: {
    title: 'New in Lineage',
    items: [
      'Relationship words in your family’s language: Spanish, Gujarati, Hindi, Punjabi or Arabic, in their own script or in English letters (beta).',
      'Tap anyone to see how you’re related, step by step.',
      'The whole app is now available in Spanish.',
      'A cleaner new typeface.',
    ],
    chooseWords: 'Choose your words',
  },
  trees: {
    settings: 'Settings',
    verifyEmail: (email: string) => `Please verify your email — we sent a link to ${email}. Some invites need a verified address.`,
    offline: 'You’re offline. Showing your last saved list.',
    yourTrees: 'Your trees',
    couldntLoad: 'Couldn’t load your trees. Check your connection and try again.',
    people: (n: number) => (n === 1 ? '1 person' : `${n} people`),
    none: 'You’re not in a tree yet. Start one, or open an invite link from a relative.',
    start: 'Start a new tree',
    treeName: 'Tree name',
    treeNameExample: 'The Chokshi Family',
    create: 'Create',
  },
  invite: {
    cantBeUsed: 'This invite can’t be used',
    invitedYou: (inviter: string) => `${inviter} invited you`,
    as: (name: string) => `as ${name}`,
    wrongAccount: 'Wrong account',
    couldntJoin: 'Couldn’t join',
    join: 'Join',
    signInToJoin: 'Sign in to join.',
    titlePerson: (name: string) => `Invite ${name}`,
    titleAnyone: 'Invite someone',
    explainPerson: (name: string) => `Anyone with the link can join as ${name}. The link works for 30 days.`,
    explainAnyone: 'Anyone with the link can join, then pick their card or add themselves. The link works for 30 days.',
    role: 'Role',
    viewer: 'Viewer',
    editor: 'Editor',
    onlyEmail: 'Only this email can join (optional)',
    onlyEmailHint: 'Leave empty for anyone with the link',
    maxUses: 'People who can use the link',
    createLink: 'Create invite link',
    link: 'Invite link',
    share: 'Share…',
    copy: 'Copy link',
    message: (tree: string) => `Join ${tree} on Lineage`,
  },
  history: {
    title: 'History',
    actions: {
      'person.create': 'Added a person',
      'person.update': 'Edited a person',
      'person.delete': 'Removed a person',
      'link.set': 'Changed parents',
      'partner.add': 'Linked partners',
      'partner.remove': 'Unlinked partners',
      import: 'Imported a backup',
      revert: 'Undid a change',
    } as Record<string, string>,
    undone: 'Undone',
    couldntUndo: 'Couldn’t undo',
    empty: 'No changes yet.',
    undo: 'Undo',
  },
  io: {
    title: 'Import & export',
    export: 'Export (JSON + photos zip)',
    import: 'Import a backup…',
    exportFailed: 'Export failed',
    importing: (people: number) => `Importing ${people} people…`,
    importingPhotos: (people: number, photos: number, done: number) => `Importing ${people} people and ${photos} photos… (${done}/${photos})`,
    couldntImport: 'That file couldn’t be imported.',
    aPhoto: 'a photo',
    couldntProcess: 'could not be processed',
    done: (people: number, relationships: number, photos: number) => `Imported ${people} people, ${relationships} relationships and ${photos} photos.`,
    skipped: (what: string, reason: string) => `Skipped ${what}: ${reason}`,
    undoHint: 'You can undo the whole import from History.',
  },
  birthdays: {
    title: 'Birthdays',
    today: 'Today 🎂',
    tomorrow: 'Tomorrow',
    inDays: (n: number) => `In ${n} days`,
    remembered: 'Remembered',
    turns: (n: number) => `Turns ${n}`,
    ageUnknown: 'Age unknown',
    includePassed: 'Include loved ones who have passed',
    empty: 'No birthdays yet. Add a full birth date (like “14 Mar 1952”) to someone’s profile and they’ll appear here.',
    comingUp: 'Coming up',
    next30: 'next 30 days',
    thisMonth: 'This month · ',
    dayMonth: (day: number, month: string) => `${day} ${month}`,
    dateOrder: (dmy: boolean) =>
      `Dates written like 02/04/1995 are read as ${dmy ? 'day/month/year' : 'month/day/year'}, matching the other dates in this tree. Editing a profile saves its date in the standard format.`,
    noBirthday: 'No birthday yet',
    noBirthdayHint: 'Open their profile and add a day and month to include them (a year alone isn’t enough).',
    born: (date: string) => `Born: ${date}`,
    noBirthDate: 'No birth date',
  },
  photo: {
    couldntOpen: 'That image couldn’t be opened. Try a JPEG or PNG.',
    couldntProcess: 'Couldn’t process that photo.',
    replace: 'Replace photo',
    add: 'Add photo',
    remove: 'Remove photo',
    crop: 'Crop photo',
    zoom: 'Zoom',
    use: 'Use photo',
  },
  members: {
    title: 'Members',
    inviteFamily: 'Invite family',
    renamed: 'Renamed',
    /** Before, between and after the two names, so they can be bold. */
    claims: (who: string, person: string): [string, string, string] => [who, ' says they are ', person],
    approve: 'Approve',
    decline: 'Decline',
    member: 'Member',
    linkedTo: (name: string) => `Linked to ${name}`,
    roleOf: (name: string) => `Role of ${name}`,
    makeOwner: 'Make owner',
    confirmTransfer: 'Transfer ownership? You will become an editor.',
    linkedPerson: 'Linked person',
    notLinked: 'Not linked',
    remove: 'Remove',
    confirmRemove: 'Remove this member?',
    leave: 'Leave tree',
    confirmLeave: 'Leave this tree?',
    activeInvites: 'Active invites',
    noInvites: 'No active invites. Tap “Invite family” for a link anyone can use, or invite a relative from their profile.',
    onlyEmail: (email: string) => `Only ${email}`,
    anyone: 'Anyone with the link',
    used: (uses: number, max: number) => `${uses}/${max} used`,
    expires: (date: string) => `expires ${date}`,
    revoke: 'Revoke',
    treeSettings: 'Tree settings',
    treeName: 'Tree name',
    rename: 'Rename',
    confirmDelete: (name: string) => `Type the tree name to delete it forever:\n${name}`,
    deleteTree: 'Delete tree',
  },
  onboarding: {
    chat: ['Has anyone got the old family photos?', 'Even better, I put us all on a tree. Find yourself here:', 'Found myself! Adding the kids now.'],
    points: ['Only people you invite', 'No ads, never sold', 'Export or delete any time'],
    slides: [
      { title: 'Your whole family, on one tree', body: 'Elders on top, children below, everyone on a portrait card with their story.' },
      { title: 'Seen from where you stand', body: 'Your card says “You”, and everyone is labelled from your side. Tap someone to see it through their eyes.' },
      { title: 'One link brings everyone in', body: 'Share it in the family chat. Relatives sign in, find their card or add themselves in a minute.' },
      { title: 'Private by design', body: 'Every tree is visible only to the family you invite. Lineage is free.' },
    ],
    skip: 'Skip',
    slideOf: (i: number, n: number) => `${i} of ${n}`,
    slides_: 'Slides',
    slide: (i: number) => `Slide ${i}`,
    getStarted: 'Get started',
    explore: 'Explore a sample family',
    next: 'Next',
  },
  tree: {
    call: 'Call:',
    callAs: (word: string) => `Call: ${word}`,
    requestSent: 'Request sent — you can already see the tree from there',
    linked: 'Linked to your account',
    removed: 'Removed. You can undo it from History.',
    notFound: 'Tree not found',
    couldntLoad: 'Couldn’t load this tree',
    notFoundHint: 'It may have been deleted, or you’re not a member.',
    backToTrees: 'Back to your trees',
    allTrees: 'All trees',
    addPerson: 'Add a person',
    add: 'Add',
    centre: 'Centre',
    fit: 'Fit',
    menu: 'Menu',
    seeAs: 'See the tree as…',
    backToMe: 'Back to me',
    birthdays: 'Birthdays',
    asList: 'View as a list',
    membersSettings: 'Members & settings',
    history: 'History',
    account: 'Account',
    offline: 'Showing your last saved copy. Editing needs a connection.',
    seeingAs: (name: string) => `Seeing the tree as ${name}`,
    promptTitle: 'See the tree from your place in it',
    promptBody: 'Pick your profile and every relative is labelled from your point of view.',
    findMyself: 'Find myself',
    notOnTree: 'I’m not on the tree yet',
    notNow: 'Not now',
    startTitle: 'Start your family tree',
    addYourself: 'Add yourself',
    trySample: 'Try a sample family',
    ownerHasntAdded: 'The owner hasn’t added anyone yet.',
    confirmRemove: (name: string) => `Remove ${name} from the tree? You can undo this from History.`,
    whoAreYou: 'Which one is you?',
    findSomeone: 'Find someone',
    familyTree: 'Family tree',
    lineKey: 'Line key',
    childOfBoth: 'child of both',
    childOfOne: 'child of one partner',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    fitScreen: 'Fit tree to screen',
    sample: 'Sample',
    sampleName: 'The Whitfield Family',
    viewingAs: (name: string) => `Viewing as ${name} · Change`,
  },
  picker: {
    alreadyLinked: ' · already linked',
    addMe: 'I’m not on the tree yet — add me',
  },
  profile: {
    label: (name: string) => `${name} profile`,
    closeProfile: 'Close profile',
    born: 'Born',
    died: 'Died',
    restingPlace: 'Resting place',
    occupation: 'Occupation',
    livesIn: 'Lives in',
    edit: 'Edit',
    invite: 'Invite',
    thisIsMe: 'This is me',
    seeAs: (name: string | null) => `See the tree as ${name ?? 'them'}`,
    addRelative: 'Add relative',
    alsoParent: (partner: string, kids: string, sex: 'female' | 'male' | null) =>
      `Is ${partner} also ${kids}’s ${sex === 'female' ? 'mother' : sex === 'male' ? 'father' : 'parent'}?`,
    and: ' and ',
    linkAll: (name: string | null) => `Link for all ${name ?? 'their'}’s children`,
    life: 'Life',
    whatToCall: (gender: string | null): string => (gender === 'female' ? 'What to call her' : gender === 'male' ? 'What to call him' : 'What to call them'),
    byName: (name: string | null) => (name ? `By name: ${name}` : 'By their first name'),
    callNote: 'Common forms; every family has its own.',
    ifYounger: (name: string | null) => `Or just ${name ?? 'their name'}, if younger than you. Add birth years to settle it.`,
    noDetails: 'No details yet.',
    family: 'Family',
    noRelatives: 'No relatives linked yet.',
    online: 'Online',
    email: 'Email',
    phone: 'Phone',
    story: 'Story',
    lastEdited: (by: string | null, date: string) => `Last edited ${by ? `by ${by} · ` : ''}${date}`,
    remove: 'Remove from tree',
  },
  form: {
    addSomeone: 'Add someone',
    addYourselfToTree: 'Add yourself to the tree',
    edit: (name: string) => `Edit ${name}`,
    addRelOf: (rel: string, name: string) => `Add ${rel} of ${name}`,
    relative: 'relative',
    addYourself: 'Add yourself',
    addPerson: 'Add a person',
    whoAdding: 'Who are you adding?',
    myself: 'Myself',
    someoneElse: 'Someone else',
    closestTo: 'Who on the tree are you closest related to?',
    relatedTo: 'Who on the tree are they related to?',
    chooseRelative: 'Choose a relative…',
    noOneYet: 'No one yet',
    youAre: (name: string) => `You are ${name}’s…`,
    theyAre: (name: string) => `They are ${name}’s…`,
    relationshipTo: (name: string) => `Relationship to ${name}`,
    relationship: 'Relationship',
    shownAsParent: (kids: string) => `They’ll be shown as the parent of ${kids}.`,
    stepParent: 'Step-parent (don’t link as parent)',
    otherParent: 'Other parent',
    notListed: 'Not listed',
    partnerOf: (name: string) => `Partner of ${name}`,
    firstName: 'First name',
    lastName: 'Last name',
    birthSurname: 'Birth surname',
    gender: 'Gender',
    notSpecified: 'Not specified',
    female: 'Female',
    male: 'Male',
    other: 'Other',
    born: 'Born',
    birthplace: 'Birthplace',
    living: 'Living',
    died: 'Died',
    restingPlace: 'Resting place',
    occupation: 'Occupation',
    livesIn: 'Lives in',
    story: 'Story',
    online: 'Online',
    email: 'Email',
    phone: 'Phone (with country code)',
    phoneHint: 'Start with + and the country code, e.g. +44 or +91.',
    linkPlaceholder: {
      instagram: '@username or link',
      facebook: 'facebook.com/…',
      linkedin: 'linkedin.com/in/…',
      twitter: '@username or link',
      website: 'https://…',
    } as Record<string, string>,
    familyLinks: 'Family links',
    parentN: (n: number) => `Parent ${n}`,
    none: 'None',
    couldntSave: 'Couldn’t save. Check your connection and try again.',
    yearExample: 'Year, e.g. 1952',
    savedAs: (text: string) => `Saved as “${text}”. Pick a date to replace it.`,
    onlyYear: 'I only know the year',
  },
  views: {
    asList: (tree: string) => `${tree} as a list`,
    view: 'View',
    modes: {
      folders: { label: 'Folders', hint: 'The tree as an outline: each person sits under their parents.' },
      age: { label: 'By age', hint: 'Oldest to youngest, by birth date.' },
      distance: { label: 'By closeness', hint: 'Nearest relatives first, counting steps along parent, child and partner links.' },
      generation: { label: 'By generation', hint: 'Everyone grouped with the people of their own generation.' },
      name: { label: 'A–Z', hint: 'Alphabetical by last name, then first.' },
      place: { label: 'By place', hint: 'Grouped by where they live (or lived).' },
    },
    birthUnknown: 'Birth date unknown',
    age: (age: number, life: string) => `${age} yrs · ${life}`,
    oldestFirst: 'Oldest first',
    youngestFirst: 'Youngest first',
    emptyTree: 'No one on the tree yet.',
    addBirth: 'Add a birth date to place them',
    pickYourself: 'Pick who you are on the tree first (menu → “See the tree as…”) and everyone will be listed by how close they are to you.',
    steps: (n: number) => (n === 1 ? '1 step away' : `${n} steps away`),
    notConnected: 'Not connected to you',
    generation: (n: number, count: number) => `Generation ${n} · ${count}`,
    noPlace: 'No place added',
    toggleChildren: (open: boolean, name: string) => `${open ? 'Collapse' : 'Expand'} ${name}’s children`,
    below: (n: number) => `${n} below`,
    partner: (sub: string) => `Partner · ${sub}`,
  },
  legal: {
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    and: 'and',
    /** Checkbox on Create account and on the agreement screen: "I agree to the [Terms] and [Privacy Policy]." */
    agreeBefore: 'I agree to the',
    agreeAfter: (age: number) => `, and I am at least ${age} years old.`,
    /** Under the sign-in buttons: "By continuing, you agree to the [Terms] and [Privacy Policy]." */
    continuingBefore: 'By continuing, you agree to the',
    mustAgree: 'Please tick the box to agree to the Terms of Service and Privacy Policy.',
    gateTitleNew: 'One last step',
    gateTitleUpdated: 'We’ve updated our terms',
    gateBody: 'Please read and agree to the Terms of Service and Privacy Policy to use Lineage. They explain what we store, the usage statistics we collect and the choices you have.',
    agreeAndContinue: 'Agree and continue',
    couldntSave: 'Couldn’t save your agreement. Please try again.',
    bannerText: 'Lineage uses analytics to see which features help families and to fix problems. It’s never used for ads and never sold. You can change this any time in Settings.',
    allow: 'Allow',
    decline: 'Decline',
    learnMore: 'Privacy Policy',
  },
  settings: {
    title: 'Settings',
    account: 'Account',
    hiddenEmail: 'Signed in with a hidden email',
    yourName: 'Your name',
    appearance: 'Appearance',
    themes: { system: 'System', light: 'Light', dark: 'Dark' },
    signOut: 'Sign out',
    privacy: 'Privacy policy',
    howDeletion: 'How account deletion works',
    deleteAccount: 'Delete account',
    deleteMyAccount: 'Delete my account…',
    deleteExplainBefore: 'This removes your account and tree memberships. People you added stay in the family trees. Type',
    deleteExplainAfter: 'to confirm.',
    confirmation: 'Confirmation',
    passwordToConfirm: 'Enter your password to confirm it’s you',
    deleteForever: 'Permanently delete',
    couldntDelete: 'Couldn’t delete the account. Please try again.',
    language: 'Language',
    appLanguage: 'App language',
    automatic: (name: string) => `Automatic (${name})`,
    words: 'Relationship words',
    wordsHint: 'The words on each card for how that person is related to you.',
    beta: 'beta',
    comingSoon: 'Coming soon',
    /** Planned languages shown as disabled entries, named in the screen language (PRD D-49). */
    upcoming: {
      gu: 'Gujarati', hi: 'Hindi', ar: 'Arabic', pt: 'Portuguese (Brazil)', ur: 'Urdu', bn: 'Bengali', zh: 'Chinese (Mandarin)',
      'pa-Arab': 'Punjabi (Shahmukhi)', ta: 'Tamil', te: 'Telugu', kn: 'Kannada', ml: 'Malayalam', mr: 'Marathi', tr: 'Turkish',
      fa: 'Persian', ko: 'Korean', vi: 'Vietnamese', id: 'Indonesian / Malay', fil: 'Filipino / Tagalog', ru: 'Russian',
      fr: 'French', ja: 'Japanese', de: 'German', it: 'Italian', sw: 'Swahili', ha: 'Hausa', yo: 'Yoruba', am: 'Amharic',
    },
    wordsAutomaticHint: 'Uses the words of the app language',
    /** Each relationship-word set: its name (in its own script where it has one) and what it is written in. */
    wordSets: {
      en: { title: 'English', how: 'English words' },
      es: { title: 'Español', how: 'Spanish words' },
      gu: { title: 'ગુજરાતી', how: 'Gujarati script' },
      'gu-Latn': { title: 'Gujarati', how: 'English letters' },
      hi: { title: 'हिन्दी', how: 'Devanagari script' },
      'hi-Latn': { title: 'Hindi', how: 'English letters' },
      pa: { title: 'ਪੰਜਾਬੀ', how: 'Gurmukhi script' },
      'pa-Latn': { title: 'Punjabi', how: 'English letters' },
      ar: { title: 'العربية', how: 'Arabic script, right to left' },
      'ar-Latn': { title: 'Arabic', how: 'English letters' },
    },
    preview: 'How Leo in the sample family sees his relatives:',
    couldntSaveChoice: 'Couldn’t save your choice. Please try again.',
    shareUsage: 'Share usage statistics',
    navProfile: 'Profile',
    navPrivacy: 'Privacy',
    groupPreferences: 'Preferences',
    backToTrees: 'Back to trees',
    signedInWith: (provider: string) => `Signed in with ${provider}`,
    providers: { google: 'Google', apple: 'Apple', password: 'email and password' },
    automaticTitle: 'Automatic',
    automaticFollows: (name: string) => `Follows your device: ${name}`,
    shareUsageHint: 'Counts of how the app is used. Never your family’s names or photos.',
    terms: 'Terms of Service',
  },
  /**
   * Fixed English messages from the server and from shared validation, by their exact text. English shows
   * them as they are; other languages translate the ones they know (PRD 11.5).
   */
  messages: {} as Record<string, string>,
  /** When a server message isn't in `messages`: a general one for its error code, if the language has it. */
  apiCodes: {} as Record<string, string>,
};

export type Messages = typeof en;
